/**
 * Tela de login com Cloudflare Turnstile.
 *
 * Fluxo:
 *  1. Usuário preenche email + senha.
 *  2. Widget Turnstile carrega e emite token ao completar desafio.
 *  3. Submit envia { email, password, turnstile_token } ao backend.
 *  4. Backend valida Turnstile server-side antes de checar credenciais.
 *  5. Em sucesso: JWT armazenado e redirecionamento pela role.
 *
 * Rate limit visível: em HTTP 429, exibe contador de espera.
 */
import { FormEvent, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Turnstile } from '@marsidev/react-turnstile'
import type { TurnstileInstance } from '@marsidev/react-turnstile'
import { useAuth } from '../lib/auth'
import { authApi, getErrorMessage } from '../lib/api'
import type { Role } from '../types/api'

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA'

const HOME_BY_ROLE: Record<Role, string> = {
  SUPER_ADMIN: '/superadmin',
  ADMIN_TENANT: '/admin',
  GESTOR_RH: '/rh',
  OPERADOR: '/rh/espelho',
  FUNCIONARIO: '/funcionario',
}

export function LoginPage() {
  const { login, isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileInstance>(null)

  // Redireciona se já autenticado.
  if (isAuthenticated && user) {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname
    navigate(from ?? HOME_BY_ROLE[user.role], { replace: true })
    return null
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    // turnstile_token pode ser null quando PONTOFLOW_TURNSTILE_ENABLED=false no backend.
    // O servidor aceita null e retorna 200; em produção com ENABLED=true, null gera 400.
    setIsLoading(true)
    try {
      const { access_token } = await authApi.login({
        email,
        password,
        turnstile_token: turnstileToken,
      })
      login(access_token)
      // Redirecionamento ocorre pelo efeito de isAuthenticated no render acima.
    } catch (err) {
      const msg = getErrorMessage(err)
      if (msg.includes('429') || msg.toLowerCase().includes('many')) {
        setError('Muitas tentativas. Aguarde um minuto e tente novamente.')
      } else if (msg.includes('400') || msg.toLowerCase().includes('bot')) {
        setError('Verificação anti-bot falhou. Recarregue a página e tente novamente.')
      } else {
        setError('E-mail ou senha inválidos.')
      }
      // Reset Turnstile para nova tentativa.
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-gray-50)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ width: '100%', maxWidth: '400px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
          <span style={{ fontSize: '2.5rem' }}>⏱</span>
          <h1
            style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 700,
              color: 'var(--color-primary)',
              marginTop: 'var(--space-2)',
            }}
          >
            PontoFlow
          </h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Controle de ponto inteligente
          </p>
        </div>

        <div className="card">
          <h2
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 600,
              marginBottom: 'var(--space-6)',
            }}
          >
            Entrar
          </h2>

          {error && (
            <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* E-mail */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label
                htmlFor="email"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  marginBottom: 'var(--space-1)',
                }}
              >
                E-mail
              </label>
              <input
                id="email"
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                disabled={isLoading}
                placeholder="seuemail@empresa.com.br"
              />
            </div>

            {/* Senha */}
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <label
                htmlFor="password"
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  marginBottom: 'var(--space-1)',
                }}
              >
                Senha
              </label>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isLoading}
                placeholder="••••••••"
              />
            </div>

            {/* Turnstile */}
            <div style={{ marginBottom: 'var(--space-6)', display: 'flex', justifyContent: 'center' }}>
              {/* Turnstile é exibido quando disponível; falha silenciosamente em dev
                  (o backend decide via PONTOFLOW_TURNSTILE_ENABLED=false).           */}
              {!turnstileError ? (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={SITE_KEY}
                  onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(false) }}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => {
                    // Widget indisponível (sem internet, firewall, ad-blocker).
                    // Em dev (ENABLED=false no backend), submissão com token=null é aceita.
                    setTurnstileToken(null)
                    setTurnstileError(true)
                  }}
                  options={{ theme: 'light', language: 'pt-br' }}
                />
              ) : (
                <div style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-warning-light)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-warning)',
                  textAlign: 'center',
                }}>
                  Verificação anti-bot indisponível (modo desenvolvimento)
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', padding: 'var(--space-3)' }}
              disabled={isLoading}
            >
              {isLoading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
        </div>

        <p
          style={{
            textAlign: 'center',
            marginTop: 'var(--space-6)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-gray-400)',
          }}
        >
          Protegido por Cloudflare Turnstile
        </p>
      </div>
    </div>
  )
}
